/* XNote proposal, group "info" (Compare, Help, 404). Deferred, no dependencies. Progressive only: without this file
   every element renders complete and visible (Help shows every topic; Compare's tables show every row and the cost
   chart is drawn; the 404 strokes are drawn by CSS alone). Motion goes through xp-motion.js (XP.motion) and respects
   reduced motion and Save-Data. */
(() => {
  'use strict';
  const d = document, w = window, root = d.documentElement;
  const motionOn = () => root.classList.contains('xp-motion') && !root.classList.contains('xp-still');
  const ready = (fn) => (d.readyState === 'loading' ? d.addEventListener('DOMContentLoaded', fn) : fn());
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[’‘]/g, "'").replace(/\s+/g, ' ');
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  /* ---------- Compare ----------
     - The XNote column lights up row by row; each head-to-head faces off as one piece as soon as its table enters
       (reveal hooks added here and watched by xp-motion.js; its 3 s safety drops .xp-motion if the engine never
       starts, so nothing can stay hidden).
     - "Cost to start": the bars grow one after another and each price rides the end of its bar (CSS transforms on
       the real, server-rendered value; nothing counts). The chart is armed here, so without this script it is drawn.
     - Phones: each comparison shows its decisive rows first (data-xp-key), with "Show all rows"; the rival links
       (the stage's list, the chart's names) open the whole matchup, and the chart's iPad / Photo names pick that
       column in the iPad table's switch (data-xp-col). */
  function compare() {
    const M = w.XP && w.XP.motion;
    const moving = !!M && motionOn();

    const matchups = [];
    d.querySelectorAll('.xp-compare-sec .xp-compare__table').forEach((box, n) => {
      const table = box.querySelector('.xp-table');
      if (!table || !table.querySelector('tbody tr[data-xp-key]') || !table.querySelector('tbody tr:not([data-xp-key])')) return;
      if (!table.id) table.id = 'xp-cmp-table-' + (n + 1);
      const btn = d.createElement('button');
      btn.type = 'button';
      btn.className = 'xp-cmp-more';
      btn.setAttribute('aria-controls', table.id);
      const set = (open) => {
        box.classList.toggle('is-short', !open);
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? 'Show fewer rows' : 'Show all rows';
      };
      btn.addEventListener('click', () => set(btn.getAttribute('aria-expanded') !== 'true'));
      table.after(btn);
      set(false);
      matchups.push({ sec: box.closest('section'), set });
    });
    const openFor = (hash) => {
      const m = matchups.find((x) => x.sec && '#' + x.sec.id === hash);
      if (m) m.set(true);
    };
    const pickCol = (a) => {
      const col = a.getAttribute('data-xp-col');
      const sec = col && d.querySelector(a.getAttribute('href'));
      const b = sec && sec.querySelector('.xp-seg button[data-col="' + col + '"]');
      if (b && b.getAttribute('aria-pressed') !== 'true') b.click();
    };
    d.querySelectorAll('.xp-cmp-stage__rival, .xp-cmp-cost__name a').forEach((a) => a.addEventListener('click', () => { openFor(a.getAttribute('href')); pickCol(a); }));
    if (location.hash) openFor(location.hash);
    w.addEventListener('hashchange', () => openFor(location.hash));

    if (!moving) return;
    d.querySelectorAll('[data-xp-cost]').forEach((chart) => {
      chart.classList.add('is-armed');
      M.watch(chart, () => chart.classList.add('is-in'), { threshold: 0.3 });
    });
    d.querySelectorAll('.xp-compare-sec--ipad .xp-compare__table').forEach((el) => {
      el.setAttribute('data-reveal', 'light');
      M.watch(el, () => el.classList.add('is-in'), { threshold: 0.2 });
    });
    /* Head-to-heads: the whole table faces off together (rows 40 ms apart) once its top edge is in view, so the raised
       card is never on screen half empty. */
    d.querySelectorAll('.xp-compare-sec--versus .xp-table').forEach((table) => {
      const rows = Array.from(table.querySelectorAll('tr'));
      rows.forEach((tr, i) => { tr.setAttribute('data-reveal', 'face'); tr.style.setProperty('--i', String(i)); });
      M.watch(table, () => rows.forEach((tr) => tr.classList.add('is-in')), { threshold: 0, margin: '0px 0px -8% 0px' });
    });
  }

  /* ---------- Help: topic cards, the question filter and deep links ----------
     The search marks the matched words in the question (inside its own span, so the summary's flex layout never
     splits the line) and in the answer; answers that hold the match open by themselves when the result is short, so
     a result never looks unrelated to the query. Items the search opened close again when the query changes. */
  function help() {
    const box = d.querySelector('[data-xp-help]');
    if (!box) return;
    const list = box.querySelector('.xp-help-list');
    const faq = list && list.querySelector('.xp-faq');
    if (!faq) return;
    const status = list.querySelector('[data-xp-help-status]');
    const empty = list.querySelector('[data-xp-help-empty]');
    const input = d.querySelector('[data-xp-help-q]');
    /* While a query is active, phones fold away the support card and the topic list (xp-info.css), so the status line
       and the first answers sit right under the field; clearing or Esc restores them. */
    const page = box.closest('main') || d.body;
    const phone = w.matchMedia('(max-width: 767px)');
    const clearBtn = d.querySelector('[data-xp-help-clear]');
    const links = Array.from(box.querySelectorAll('.xp-help-topic'));
    const groups = [];
    faq.querySelectorAll(':scope > .xp-faq__group').forEach((h) => {
      const acc = h.nextElementSibling;
      if (!acc || !acc.classList.contains('xp-accordion')) return;
      const items = Array.from(acc.querySelectorAll(':scope > details.xp-acc')).map((det) => {
        const sum = det.querySelector('summary');
        const q = sum.textContent.trim();
        const qs = d.createElement('span');
        qs.className = 'xp-help-q';
        qs.textContent = q;
        sum.textContent = '';
        sum.appendChild(qs);
        const ans = det.querySelector('.xp-acc__a');
        return { det, qs, q, qn: norm(q), ans, an: norm(ans ? ans.textContent : ''), auto: false };
      });
      h.tabIndex = -1;
      groups.push({ key: h.id.replace(/^faq-/, ''), h, acc, items, link: links.find((a) => a.dataset.topic === h.id.replace(/^faq-/, '')) });
    });
    if (!groups.length) return;
    const byKey = new Map(groups.map((g) => [g.key, g]));
    const count = (n) => (n === 1 ? '1 question' : n + ' questions');
    /* The count as a number and its word ("10" + " questions"): the desktop index shows the number alone, right-aligned
       (the word stays for screen readers); phones show both. */
    const setN = (g, n) => {
      const s = g.link && g.link.querySelector('[data-topic-n]');
      if (!s) return;
      const num = d.createElement('span');
      num.className = 'xp-help-topic__num';
      num.textContent = String(n);
      const word = d.createElement('span');
      word.className = 'xp-help-topic__word';
      word.textContent = n === 1 ? ' question' : ' questions';
      s.replaceChildren(num, word);
    };
    links.forEach((a) => { if (!byKey.has(a.dataset.topic)) a.parentElement.hidden = true; });
    let topic = byKey.has(box.dataset.open) ? box.dataset.open : 'all';
    let terms = [];

    const hit = (text) => { const m = d.createElement('mark'); m.className = 'xp-help-hit'; m.textContent = text; return m; };
    const unmark = (el) => {
      const marks = el.querySelectorAll('mark.xp-help-hit');
      if (!marks.length) return;
      marks.forEach((m) => m.replaceWith(d.createTextNode(m.textContent)));
      el.normalize();
    };
    const mark = (el, re) => {
      const walker = d.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const parts = node.nodeValue.split(re);
        if (parts.length < 2) return;
        const frag = d.createDocumentFragment();
        parts.forEach((part, i) => { if (part) frag.appendChild(i % 2 ? hit(part) : d.createTextNode(part)); });
        node.replaceWith(frag);
      });
    };
    /* A verbatim excerpt of the answer around the first match ("…a notebook and the app…"), shown under a closed
       question whose own text doesn't hold the words, so the result explains itself. */
    const snippet = (it, re) => {
      if (!it.ans) return null;
      const bits = [];
      const tw = d.createTreeWalker(it.ans, NodeFilter.SHOW_TEXT);
      while (tw.nextNode()) bits.push(tw.currentNode.nodeValue);
      const raw = bits.join(' ').replace(/\s+/g, ' ').replace(/\s+([.,;:!?)])/g, '$1').trim();
      re.lastIndex = 0;
      const m = re.exec(raw);
      if (!m) return null;
      let a = Math.max(0, m.index - 36), b = Math.min(raw.length, m.index + m[0].length + 64);
      if (a > 0) { const sp = raw.indexOf(' ', a); a = sp > -1 && sp < m.index ? sp + 1 : a; }
      if (b < raw.length) { const sp = raw.lastIndexOf(' ', b); b = sp > m.index + m[0].length ? sp : b; }
      const el = d.createElement('span');
      el.className = 'xp-help-snip';
      const text = (a > 0 ? '…' : '') + raw.slice(a, b) + (b < raw.length ? '…' : '');
      text.split(re).forEach((part, i) => { if (part) el.appendChild(i % 2 ? hit(part) : d.createTextNode(part)); });
      return el;
    };
    const paint = (it, on, re) => {
      if (it.qs.childElementCount) it.qs.textContent = it.q;
      if (it.ans) unmark(it.ans);
      if (!re || !on) return;
      it.qs.textContent = '';
      it.q.split(re).forEach((part, i) => { if (part) it.qs.appendChild(i % 2 ? hit(part) : d.createTextNode(part)); });
      if (!terms.every((t) => it.qn.includes(t))) { const sn = snippet(it, re); if (sn) it.qs.appendChild(sn); }
      if (it.ans) mark(it.ans, re);
    };
    const show = () => {
      let total = 0;
      const re = terms.length ? new RegExp('(' + terms.map(esc).join('|') + ')', 'gi') : null;
      const hits = [];
      groups.forEach((g) => {
        let n = 0;
        g.items.forEach((it) => {
          const on = !terms.length || terms.every((t) => it.qn.includes(t) || it.an.includes(t));
          it.det.hidden = !on;
          paint(it, on, re);
          if (it.auto) { it.det.open = false; it.auto = false; }
          if (on) { n += 1; if (terms.length) hits.push(it); }
        });
        const vis = terms.length ? n > 0 : topic === 'all' || topic === g.key;
        g.h.hidden = !vis;
        g.acc.hidden = !vis;
        total += n;
        setN(g, n);
        if (g.link) {
          g.link.classList.toggle('is-none', terms.length > 0 && n === 0);
          if (!terms.length && topic === g.key) g.link.setAttribute('aria-current', 'true'); else g.link.removeAttribute('aria-current');
        }
      });
      // Short results open themselves: every hit when there are three or fewer; otherwise, when there are at most
      // eight, the hits whose question doesn't hold every word (the match is in the answer).
      hits.forEach((it) => {
        const inQ = terms.every((t) => it.qn.includes(t));
        if (!it.det.open && (total <= 3 || (!inQ && total <= 8))) { it.det.open = true; it.auto = true; }
      });
      box.classList.toggle('is-searching', terms.length > 0);
      page.classList.toggle('xp-help-searching', terms.length > 0);
      if (empty) empty.hidden = !(terms.length && total === 0);
      if (status) status.textContent = terms.length ? count(total) : '';
      if (clearBtn) clearBtn.hidden = !(input && input.value);
    };
    const inView = (el) => { const r = el.getBoundingClientRect(); return r.top >= 72 && r.top < w.innerHeight * 0.6; };
    const go = (el, focus) => {
      if (!inView(el)) el.scrollIntoView({ block: 'start', behavior: motionOn() ? 'smooth' : 'auto' });
      if (focus) el.focus({ preventScroll: true });
    };
    const clear = () => { terms = []; if (input) input.value = ''; };
    const select = (key) => {
      const g = byKey.get(key);
      if (!g) return;
      clear();
      topic = key;
      show();
      go(g.h, true);
      if (history.replaceState) history.replaceState(null, '', '#faq-' + key);
    };
    links.forEach((a) => a.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      select(a.dataset.topic);
    }));
    let timer = 0;
    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(timer);
        if (clearBtn) clearBtn.hidden = !input.value;
        timer = setTimeout(() => {
          const v = norm(input.value).trim();
          terms = v.length >= 2 ? v.split(' ').filter(Boolean) : [];
          show();
        }, 120);
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && input.value) { e.preventDefault(); clear(); show(); return; }
        if (e.key !== 'Enter') return;
        /* Enter (the keyboard's Search key, enterkeyhint="search"): run the query now; on phones the field lets go so
           the keyboard closes, and the first answer is brought up under the header if it isn't already in view. */
        e.preventDefault();
        clearTimeout(timer);
        const v = norm(input.value).trim();
        terms = v.length >= 2 ? v.split(' ').filter(Boolean) : [];
        show();
        const first = groups.reduce((f, g) => f || g.items.find((it) => !it.det.hidden && terms.length), null);
        if (!first) return;
        if (phone.matches) input.blur();
        if (first.det.getBoundingClientRect().top > w.innerHeight - 140) first.det.scrollIntoView({ block: 'start', behavior: motionOn() ? 'smooth' : 'auto' });
      });
      input.form && input.form.addEventListener('submit', (e) => e.preventDefault());
    }
    if (clearBtn) clearBtn.addEventListener('click', () => { clear(); show(); if (input) input.focus(); });
    /* Deep links (#faq-<group> or #faq-<question>, e.g. #faq-f-s5): open its topic and question. */
    const openHash = () => {
      let id = '';
      try { id = decodeURIComponent(location.hash.slice(1)); } catch (e) { return false; }
      const el = id && d.getElementById(id);
      if (!el || !faq.contains(el)) return false;
      const g = groups.find((x) => x.h === el || x.acc.contains(el));
      if (!g) return false;
      clear();
      topic = g.key;
      show();
      const det = el.closest('details.xp-acc');
      if (det) det.open = true;
      el.scrollIntoView({ block: 'start' });
      return true;
    };
    box.classList.add('is-live');
    if (!openHash()) show();
    w.addEventListener('hashchange', openHash);
  }

  /* ---------- 404: the pen writes "404" ----------
     The strokes draw themselves in CSS (each path's --at / --d); this keeps the pen's nib on the point being drawn,
     read from the running CSS animations every frame (so the two can't drift), with a soft point of light under it.
     When the underline is done the dot goes out, the inline position is dropped (the pen eases to its CSS rest pose
     past the end of the underline while it lifts and settles: .is-done). Only with motion and
     without Save-Data; otherwise the word is simply written and the pen rests (CSS). */
  function ink404() {
    const svg = d.querySelector('[data-xp-404-ink]');
    if (!svg || !motionOn() || root.classList.contains('xp-lite') || typeof svg.getAnimations !== 'function') return;
    const tip = svg.querySelector('.xp-404__tip');
    const stage = svg.closest('.xp-404');
    const pen = stage && stage.querySelector('.xp-404__pen');
    const segs = Array.from(svg.querySelectorAll('.xp-404__strokes path, .xp-404__under')).map((p) => {
      const cs = getComputedStyle(p);
      return { p, len: p.getTotalLength(), at: parseFloat(cs.getPropertyValue('--at')) || 0, dur: parseFloat(cs.getPropertyValue('--d')) || 1, a: p.getAnimations()[0] };
    }).filter((s) => s.a);
    if (!tip || !segs.length) return;
    const vb = svg.viewBox.baseVal;
    let k = 1;
    const measure = () => { k = vb && vb.width ? svg.getBoundingClientRect().width / vb.width : 1; };
    measure();
    w.addEventListener('resize', measure, { passive: true });
    let last = null;
    const end = segs[segs.length - 1];
    const clock = end.a; // the last stroke's animation runs longest: one clock for every stroke (they start together)
    if (stage) stage.classList.add('is-writing');
    const place = (pt) => {
      tip.setAttribute('transform', 'translate(' + pt.x.toFixed(1) + ' ' + pt.y.toFixed(1) + ')');
      if (pen) { pen.style.setProperty('--nx', (pt.x * k).toFixed(1) + 'px'); pen.style.setProperty('--ny', (pt.y * k).toFixed(1) + 'px'); }
    };
    const frame = () => {
      const t = Number(clock.currentTime) || 0;
      let cur = null;
      for (const s of segs) { if (t >= s.at && t <= s.at + s.dur) { cur = s; break; } if (t > s.at + s.dur) last = s; }
      const s = cur || last;
      if (s) {
        const f = cur ? (t - s.at) / s.dur : 1;
        place(s.p.getPointAtLength(s.len * Math.min(1, Math.max(0, f))));
        tip.classList.toggle('is-lift', !cur);
        tip.classList.add('is-on');
      } else {
        place(segs[0].p.getPointAtLength(0)); // before the first stroke: the nib waits at its start
      }
      if (t < end.at + end.dur && clock.playState !== 'finished') w.requestAnimationFrame(frame);
      else {
        tip.classList.remove('is-on');
        svg.classList.add('is-done');
        w.removeEventListener('resize', measure);
        if (pen) { pen.style.removeProperty('--nx'); pen.style.removeProperty('--ny'); }
        if (stage) { stage.classList.remove('is-writing'); stage.classList.add('is-done'); }
      }
    };
    w.requestAnimationFrame(frame);
  }

  ready(() => { compare(); help(); ink404(); });
})();
